// Détection de plateforme pour l'installation de l'appli (portée de flowAI,
// src/lib/pwa-platform.ts), complétée des navigateurs intégrés à une autre appli (Gmail,
// Facebook, Instagram, WhatsApp…), qui ne peuvent pas installer. Fonctions pures, plus des
// accès window/localStorage qui ne lèvent jamais.

/** « installee » : l'appli tourne déjà depuis l'écran d'accueil. */
export type Plateforme =
  'ios-safari' | 'ios-autre' | 'android' | 'integre' | 'ordinateur' | 'installee'

type Nav = { userAgent?: string; maxTouchPoints?: number }

export const CLE_REFUS_INSTALLATION = 'pmvb-installation-refusee-le'
/** Un refus d'installation est respecté une semaine. */
export const DUREE_REFUS_MS = 7 * 24 * 60 * 60 * 1000

/** iPhone ou iPad (l'iPad récent se déclare « Macintosh » : rattrapé par le tactile). */
export function estIos(nav: Nav): boolean {
  const ua = nav.userAgent ?? ''
  if (/iPhone|iPad|iPod/i.test(ua)) return true
  return /Macintosh/i.test(ua) && (nav.maxTouchPoints ?? 0) > 1
}

/** Jetons des navigateurs intégrés à une autre appli (webviews). */
const INTEGRES =
  /(FBAN|FBAV|FBIOS|FB_IAB|Instagram|Line\/|MicroMessenger|Snapchat|Twitter|LinkedInApp|WhatsApp|GSA\/|Gmail|; wv\))/i

/** Navigateur intégré à une autre appli : il ne peut pas installer l'appli. */
export function estNavigateurIntegre(nav: Nav): boolean {
  return INTEGRES.test(nav.userAgent ?? '')
}

/**
 * Vrai Safari iOS, seul capable d'« Ajouter à l'écran d'accueil » : signature
 * « Version/x … Safari » sans jeton d'un autre navigateur (CriOS, FxiOS, EdgiOS…).
 */
export function estSafariIos(nav: Nav): boolean {
  if (!estIos(nav)) return false
  const ua = nav.userAgent ?? ''
  if (/(CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo)/i.test(ua) || estNavigateurIntegre(nav)) return false
  return /Version\/[\d.]+.*Safari/i.test(ua)
}

export function detecterPlateforme(nav: Nav): Plateforme {
  if (estNavigateurIntegre(nav)) return 'integre'
  if (estIos(nav)) return estSafariIos(nav) ? 'ios-safari' : 'ios-autre'
  if (/Android/i.test(nav.userAgent ?? '')) return 'android'
  return 'ordinateur'
}

/** L'appli tourne-t-elle installée (écran d'accueil) ? */
export function estInstallee(): boolean {
  if (typeof window === 'undefined') return false
  const ios = (window.navigator as { standalone?: boolean }).standalone === true
  const mq =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches
  return ios || mq
}

export function refusInstallationValable(maintenant = Date.now()): boolean {
  try {
    const brut = window.localStorage.getItem(CLE_REFUS_INSTALLATION)
    if (!brut) return false
    const le = Number(brut)
    return Number.isFinite(le) && maintenant - le < DUREE_REFUS_MS
  } catch {
    return false
  }
}

export function memoriserRefusInstallation(maintenant = Date.now()): void {
  try {
    window.localStorage.setItem(CLE_REFUS_INSTALLATION, String(maintenant))
  } catch {
    // stockage indisponible : la proposition reviendra, sans gravité
  }
}

/**
 * Lien qui rouvre une adresse de l'appli hors du navigateur intégré à une autre appli, où Google
 * refuse la connexion : sur Android, une intention que prend le navigateur par défaut ou l'appli
 * installée ; sur iPhone, le schéma de Safari (iOS 17 et plus). Ailleurs, aucun : copier le lien.
 */
export function lienHorsNavigateurIntegre(adresse: string, nav: Nav): string | null {
  let u: URL
  try {
    u = new URL(adresse)
  } catch {
    return null
  }
  if (u.protocol !== 'https:') return null
  const reste = `${u.host}${u.pathname}${u.search}`
  if (/Android/i.test(nav.userAgent ?? ''))
    return `intent://${reste}#Intent;scheme=https;S.browser_fallback_url=${encodeURIComponent(u.href)};end`
  if (estIos(nav)) return `x-safari-https://${reste}`
  return null
}
